/**
 * Company: SPHMedia
 * Description: [Alert block component for myBH notifications]
 */

import bellIconData from "@assets/icons/animation-icons/bell-icon.json";
import ToggleButton from "@elements/Buttons/ToggleButton";
import { lazy, ReactElement, useEffect, useRef, useState } from "react";
// Lazy load Lottie powered animation to avoid including heavy library and animation data in main bundle
// - currently this means the animation will be loaded client-side only
const BellIcon = lazy(
  () => import("@components/Blocks/AnimationPlayer/AnimationPlayer"),
);

export type MyBHAlertBlockProps = {
  onToggle: (enabled: boolean) => void;
  defaultEnabled?: boolean;
  uniqueKey?: string;
};

/**
 * MyBHAlertBlock
 * Description: [myBH alert block component]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function MyBHAlertBlock({
  onToggle,
  defaultEnabled = false,
  uniqueKey = "1",
}: MyBHAlertBlockProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      key={`mybh-alert-block-${uniqueKey}`}
      data-testid="mybh-alert-block"
      className="flex w-full flex-col items-center md:max-w-[381px]"
    >
      <div
        data-testid="alert-icon"
        className="bg-lightBlue-300 rounded-full w-[50px] h-[51px] shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] z-10"
      >
        <div ref={containerRef}>
          {isVisible && (
            <BellIcon
              key={`bell-icon-${uniqueKey}`}
              src={bellIconData}
              delay={Number(uniqueKey) * 50}
            />
          )}
        </div>
      </div>
      <div className="-mt-6 w-full flex gap-4 justify-center items-center px-6 py-7 bg-brand-primary">
        <p
          data-testid="alert-message"
          className="text-text-inverse text-sm font-semibold leading-[150%] font-primary max-w-[216px]"
        >
          Dapat notifikasi segera untuk berita dan topik pilihan anda
        </p>
        <ToggleButton
          key={`toggle-button-${uniqueKey}`}
          onToggle={onToggle}
          defaultEnabled={defaultEnabled}
        />
      </div>
    </div>
  );
}
